import React, { useState } from "react";
import { Link, useParams } from "wouter";
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  Camera,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  FileCheck,
  FileText,
  GraduationCap,
  Gavel,
  MessageSquare,
  Mic,
  Pencil,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
  Users,
  Video,
  Wrench,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { StandardTabsHeader } from "@/components/StandardTabsHeader";
import { useDemoStore } from "@/store/useDemoStore";
import type { Criterio, Element as StandardElement, Evidencia, Instrumento } from "@/data";
import type { Rule } from "@/data/seed";

const EMPTY_ELEMS: StandardElement[] = [];
const EMPTY_INS: Instrumento[] = [];
const EMPTY_RULES: Rule[] = [];

const tipoOrder: Criterio["tipo"][] = ["desempeño", "producto", "conocimiento", "actitud", "emergencia"];
const tipoLabel: Record<Criterio["tipo"], string> = {
  desempeño: "Criterios de desempeño",
  producto: "Criterios de producto",
  conocimiento: "Criterios de conocimiento",
  actitud: "Criterios de actitud",
  emergencia: "Respuesta ante situaciones de emergencia",
};
const tipoTone: Record<Criterio["tipo"], "teal" | "navy" | "amber" | "green"> = {
  desempeño: "teal",
  producto: "navy",
  conocimiento: "amber",
  actitud: "green",
  emergencia: "amber",
};

const evidenceIcons: Record<Evidencia["tipo"], React.ComponentType<{ size?: number; color?: string }>> = {
  video: Video,
  foto: Camera,
  documento: FileText,
  audio: Mic,
};

const instrumentIcons: Record<Instrumento["tipo"], React.ComponentType<{ size?: number; color?: string }>> = {
  checklist: ClipboardCheck,
  rúbrica: FileCheck,
  examen: BookOpen,
  observación: Eye,
};

type SubTab =
  | "datos"
  | "criterios"
  | "instrumentos"
  | "reglas"
  | "ia"
  | "vista-previa";

const subTabs: { id: SubTab; label: string }[] = [
  { id: "datos", label: "Datos del elemento" },
  { id: "criterios", label: "Criterios" },
  { id: "instrumentos", label: "Instrumentos" },
  { id: "reglas", label: "Reglas" },
  { id: "ia", label: "IA contextual" },
  { id: "vista-previa", label: "Vista previa" },
];

export default function ElementsTab() {
  const params = useParams<{ codigo: string }>();
  const codigo = params.codigo ?? "";

  const standard = useDemoStore((s) => s.standards[codigo]);
  const elems = useDemoStore((s) => s.elementsByStandard[codigo]) ?? EMPTY_ELEMS;
  const instruments = useDemoStore((s) => s.instrumentsByStandard[codigo]) ?? EMPTY_INS;
  const rules = useDemoStore((s) => s.rulesByStandard[codigo]) ?? EMPTY_RULES;

  const [selectedId, setSelectedId] = useState<string | null>(elems[0]?.id ?? null);
  const [subTab, setSubTab] = useState<SubTab>("datos");

  // Si la lista de elementos cambia y la selección no es válida, ajustar
  const validSelected = selectedId && elems.some((e) => e.id === selectedId) ? selectedId : elems[0]?.id ?? null;
  const element = validSelected ? elems.find((e) => e.id === validSelected) : undefined;

  if (!standard) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo, "Elementos"]}>
        <Card>
          <SectionTitle>Estándar no encontrado</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-600)", marginBottom: 12 }}>
            No existe un estándar con código <strong>{codigo}</strong> en el catálogo.
          </div>
          <Link href="/admin/estandares" data-testid="link-back-list">
            <PrimaryButton variant="outline">Volver al catálogo</PrimaryButton>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb={["Estándares", standard.codigo, "Elementos"]}>
      <StandardTabsHeader standard={standard} active="elementos" />

      {elems.length === 0 ? (
        <Card>
          <SectionTitle>Sin elementos configurados</SectionTitle>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)", marginBottom: 12 }}>
            Este estándar todavía no tiene elementos. Vuelve a la pantalla del estándar para crearlos o usa el wizard de alta.
          </div>
          <Link href={`/admin/estandares/${codigo}`}>
            <PrimaryButton variant="outline">Volver al estándar</PrimaryButton>
          </Link>
        </Card>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "flex-start" }}>
          {/* Lista de elementos (maestro) */}
          <Card>
            <SectionTitle>Elementos del estándar</SectionTitle>
            <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginBottom: 10 }}>
              Selecciona un elemento para ver todo su contenido a la derecha.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {elems.map((e) => {
                const isSelected = e.id === validSelected;
                const insCount = instruments.filter((i) => i.elementoId === e.id).length;
                return (
                  <button
                    key={e.id}
                    onClick={() => { setSelectedId(e.id); setSubTab("datos"); }}
                    data-testid={`element-card-${e.codigo}`}
                    style={{
                      textAlign: "left",
                      padding: 12,
                      borderRadius: 10,
                      cursor: "pointer",
                      border: isSelected ? "2px solid var(--cc-teal)" : "1px solid var(--cc-gray-200)",
                      background: isSelected ? "var(--cc-teal-bg)" : "white",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{
                        background: isSelected ? "var(--cc-teal)" : "var(--cc-navy)",
                        color: "white",
                        padding: "3px 8px",
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 11,
                      }}>{e.codigo}</span>
                      {typeof e.orden === "number" && (
                        <span style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>Orden {e.orden}</span>
                      )}
                      {e.estadoElemento && (
                        <Badge tone={e.estadoElemento === "activo" ? "green" : "amber"}>{e.estadoElemento}</Badge>
                      )}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)", lineHeight: 1.4, marginBottom: 6 }}>
                      {e.titulo}
                    </div>
                    {e.tipoEvaluacionPrincipal && (
                      <div style={{ fontSize: 10, color: "var(--cc-gray-600)", marginBottom: 6, lineHeight: 1.4 }}>
                        {e.tipoEvaluacionPrincipal}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 10, fontSize: 10, color: "var(--cc-gray-500)", flexWrap: "wrap" }}>
                      <span><strong style={{ color: "var(--cc-navy)" }}>{e.criterios.length}</strong> criterios</span>
                      <span><strong style={{ color: "var(--cc-navy)" }}>{e.evidencias.length}</strong> evidencias</span>
                      <span><strong style={{ color: "var(--cc-navy)" }}>{insCount}</strong> instrumentos</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Detalle del elemento seleccionado */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {element ? (
              <>
                {/* Cabecera del elemento */}
                <Card>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <Badge tone="teal">{element.codigo}</Badge>
                        {typeof element.orden === "number" && (
                          <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Elemento {element.orden} de {elems.length}</span>
                        )}
                      </div>
                      <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 6 }}>
                        {element.titulo}
                      </h2>
                      <div style={{ fontSize: 12, color: "var(--cc-gray-600)", lineHeight: 1.5 }}>
                        {element.descripcion}
                      </div>
                    </div>
                    <Link href={`/admin/estandares/${codigo}/elementos/${element.id}/criterios`} data-testid={`button-edit-${element.codigo}`}>
                      <PrimaryButton variant="outline"><Pencil size={13} /> Editar elemento</PrimaryButton>
                    </Link>
                  </div>
                </Card>

                {/* Sub-pestañas del elemento */}
                <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--cc-gray-200)", flexWrap: "wrap" }}>
                  {subTabs.map((st) => (
                    <button
                      key={st.id}
                      onClick={() => setSubTab(st.id)}
                      data-testid={`subtab-${st.id}`}
                      style={subTabStyle(st.id === subTab)}
                    >
                      {st.label}
                    </button>
                  ))}
                </div>

                {/* Contenido del sub-tab */}
                {subTab === "datos" && <DatosTab element={element} standardCode={codigo} totalElements={elems.length} />}
                {subTab === "criterios" && <CriteriosTab element={element} />}
                {subTab === "instrumentos" && <InstrumentosTab element={element} instruments={instruments} standardCode={codigo} />}
                {subTab === "reglas" && <ReglasTab rules={rules} />}
                {subTab === "ia" && <IaTab element={element} />}
                {subTab === "vista-previa" && <VistaPreviaTab element={element} instruments={instruments} />}
              </>
            ) : (
              <Card>
                <SectionTitle>Selecciona un elemento</SectionTitle>
                <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
                  Elige uno de los elementos de la lista para ver su detalle, criterios, instrumentos y reglas asociadas.
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}

// ============== Sub-tabs ==============

function DatosTab({ element, standardCode, totalElements }: { element: StandardElement; standardCode: string; totalElements: number }) {
  const rows: { label: string; value: string; fuente: string }[] = [
    { label: "Estándar asociado", value: standardCode, fuente: "Automático" },
    { label: "Orden del elemento", value: `${element.orden ?? "—"} de ${totalElements}`, fuente: "Viene del estándar" },
    { label: "Código del elemento", value: element.codigo, fuente: "Viene del estándar" },
    { label: "Nombre del elemento", value: element.titulo, fuente: "Viene del estándar" },
    { label: "Descripción operativa", value: element.descripcion || "—", fuente: "Configuración interna" },
    { label: "Tipo principal de evaluación", value: element.tipoEvaluacionPrincipal ?? "—", fuente: "Inferido del estándar" },
    { label: "Estado interno", value: element.estadoElemento === "activo" ? "Activo" : "Borrador", fuente: "Campo del sistema" },
    { label: "Criterios asociados", value: String(element.criterios.length), fuente: "Inferido del estándar" },
    { label: "Instrumentos para evidencias", value: String(element.evidencias.length), fuente: "Inferido del estándar" },
    { label: "Términos en glosario", value: String(element.glosario?.length ?? 0), fuente: "Inferido del estándar" },
  ];
  return (
    <Card>
      <SectionTitle>Datos del elemento</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 160px", rowGap: 8, columnGap: 10, fontSize: 12 }}>
        <div style={{ fontWeight: 700, color: "var(--cc-gray-600)", fontSize: 11, textTransform: "uppercase" }}>Campo</div>
        <div style={{ fontWeight: 700, color: "var(--cc-gray-600)", fontSize: 11, textTransform: "uppercase" }}>Valor</div>
        <div style={{ fontWeight: 700, color: "var(--cc-gray-600)", fontSize: 11, textTransform: "uppercase" }}>Fuente</div>
        {rows.map((r) => (
          <DataRowFragment key={r.label} {...r} />
        ))}
      </div>
    </Card>
  );
}

function DataRowFragment({ label, value, fuente }: { label: string; value: string; fuente: string }) {
  return (
    <>
      <div style={{ borderTop: "1px solid var(--cc-gray-100)", padding: "8px 0", color: "var(--cc-gray-700)" }}>{label}</div>
      <div style={{ borderTop: "1px solid var(--cc-gray-100)", padding: "8px 0", color: "var(--cc-navy)", fontWeight: 500, lineHeight: 1.5 }}>{value}</div>
      <div style={{ borderTop: "1px solid var(--cc-gray-100)", padding: "8px 0" }}>
        <Badge tone="gray">{fuente}</Badge>
      </div>
    </>
  );
}

function CriteriosTab({ element }: { element: StandardElement }) {
  const [openCrit, setOpenCrit] = useState<Criterio | null>(null);
  const grouped: Record<Criterio["tipo"], Criterio[]> = {
    desempeño: [], producto: [], conocimiento: [], actitud: [], emergencia: [],
  };
  for (const c of element.criterios) grouped[c.tipo].push(c);
  const totalPond = element.criterios.reduce((a, c) => a + c.ponderacion, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <SectionTitle>Criterios del elemento</SectionTitle>
          <span style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>
            Total <strong style={{ color: "var(--cc-navy)" }}>{element.criterios.length}</strong> criterios · Ponderación acumulada <strong style={{ color: "var(--cc-navy)" }}>{totalPond}%</strong>
          </span>
        </div>
      </Card>
      {tipoOrder.map((t) => {
        const items = grouped[t];
        if (items.length === 0) return null;
        const pond = items.reduce((a, c) => a + c.ponderacion, 0);
        return (
          <Card key={t} testId={`criterios-${t}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge tone={tipoTone[t]}>{items.length}</Badge>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{tipoLabel[t]}</span>
              </div>
              <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>Pond. {pond}%</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((c) => (
                <button
                  key={c.id}
                  data-testid={`row-criterio-${c.codigo}`}
                  onClick={() => setOpenCrit(c)}
                  style={{ padding: 10, border: "1px solid var(--cc-gray-100)", borderRadius: 8, display: "flex", gap: 10, alignItems: "flex-start", background: "white", cursor: "pointer", textAlign: "left", width: "100%" }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)", width: 36, flexShrink: 0 }}>{c.codigo}</div>
                  <div style={{ flex: 1, fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
                    {c.nombreCorto && (
                      <div style={{ fontWeight: 700, color: "var(--cc-navy)", marginBottom: 2 }}>{c.nombreCorto}</div>
                    )}
                    <div>{c.descripcion}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                      {c.critico && <Badge tone="amber">Crítico</Badge>}
                      {c.formaEvaluacion && <Badge tone="teal">{c.formaEvaluacion}</Badge>}
                      {c.requiereEvidencia && <Badge tone="navy">Requiere evidencia</Badge>}
                      {c.observacionObligatoria && <Badge tone="gray">Obs. obligatoria</Badge>}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                    <div style={{ width: 56, textAlign: "right", fontSize: 12, color: "var(--cc-gray-700)", fontWeight: 600 }}>{c.ponderacion}%</div>
                    <span style={{ fontSize: 10, color: "var(--cc-teal)", fontWeight: 600 }}>Ver ficha →</span>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        );
      })}
      {element.glosario && element.glosario.length > 0 && (
        <Card testId="glosario-elemento">
          <SectionTitle>Glosario del elemento ({element.glosario.length} términos)</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>
            {element.glosario.map((g, i) => (
              <div key={i}>
                <strong style={{ color: "var(--cc-navy)" }}>{g.termino}.</strong> {g.definicion}
              </div>
            ))}
          </div>
        </Card>
      )}
      {openCrit && <CriterioFichaModal criterio={openCrit} elementoCodigo={element.codigo} onClose={() => setOpenCrit(null)} />}
    </div>
  );
}

function CriterioFichaModal({ criterio, elementoCodigo, onClose }: { criterio: Criterio; elementoCodigo: string; onClose: () => void }) {
  const tipoLbl: Record<Criterio["tipo"], string> = {
    desempeño: "Desempeño", producto: "Producto", conocimiento: "Conocimiento", actitud: "Actitud", emergencia: "Emergencia",
  };
  const yn = (v: boolean | undefined) => (v ? "Sí" : "No");
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: "Elemento asociado", value: elementoCodigo },
    { label: "Código del criterio", value: criterio.codigo },
    { label: "Tipo de criterio", value: tipoLbl[criterio.tipo] },
    { label: "Nombre corto", value: criterio.nombreCorto ?? "—" },
    { label: "Texto del estándar", value: criterio.descripcion },
    { label: "Criterio obligatorio", value: yn(criterio.obligatorio ?? true) },
    { label: "Criterio crítico", value: yn(criterio.critico) },
    { label: "Forma de evaluación", value: criterio.formaEvaluacion ?? "—" },
    { label: "Requiere evidencia", value: yn(criterio.requiereEvidencia) },
    { label: "Tipo de evidencia", value: (criterio.tiposEvidencia ?? []).join(", ") || "—" },
    { label: "Observación obligatoria", value: yn(criterio.observacionObligatoria) },
    { label: "Resultado permitido", value: (criterio.resultadosPermitidos ?? []).join(" · ") || "—" },
    { label: "Instrumento asociado", value: criterio.instrumentoAsociado ?? "—" },
    { label: "Módulo LMS relacionado", value: criterio.moduloLMS ?? "—" },
    { label: "Regla IA", value: criterio.reglaIA ?? "—" },
    { label: "Ponderación", value: `${criterio.ponderacion}%` },
  ];
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 24 }}
      data-testid="modal-overlay-criterio"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ background: "white", borderRadius: 12, width: "min(760px, 100%)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)", border: "1px solid var(--cc-gray-200)" }}
        data-testid="ficha-criterio"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--cc-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Badge tone="teal">{criterio.codigo}</Badge>
            <div style={{ fontSize: 15, fontWeight: 700, color: "var(--cc-navy)" }}>
              {criterio.nombreCorto ?? criterio.descripcion.slice(0, 60)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-gray-500)" }} aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", rowGap: 10, columnGap: 12, fontSize: 12 }}>
            {rows.map((r) => (
              <React.Fragment key={r.label}>
                <div style={{ fontWeight: 700, color: "var(--cc-gray-600)", textTransform: "uppercase", fontSize: 10, letterSpacing: 0.4, paddingTop: 4 }}>{r.label}</div>
                <div style={{ color: "var(--cc-navy)", lineHeight: 1.5, borderBottom: "1px solid var(--cc-gray-100)", paddingBottom: 8 }}>{r.value}</div>
              </React.Fragment>
            ))}
          </div>
          {criterio.subitems && criterio.subitems.length > 0 && (
            <div style={{ marginTop: 16, padding: 12, background: "var(--cc-gray-50, #f8fafc)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-gray-600)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Sub-incisos del criterio</div>
              <ul style={{ margin: 0, paddingLeft: 18, color: "var(--cc-gray-700)", fontSize: 12, lineHeight: 1.5 }}>
                {criterio.subitems.map((s, i) => (<li key={i}>{s}</li>))}
              </ul>
            </div>
          )}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)" }}>
          <PrimaryButton size="sm" variant="outline" onClick={onClose}>Cerrar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function InstrumentosTab({ element, instruments, standardCode }: { element: StandardElement; instruments: Instrumento[]; standardCode: string }) {
  const upsertInstrument = useDemoStore((s) => s.upsertInstrument);
  const removeInstrument = useDemoStore((s) => s.removeInstrument);
  const own = instruments.filter((i) => i.elementoId === element.id);
  const cubiertos = new Set<string>();
  for (const ins of own) for (const cid of ins.criterioIds ?? []) cubiertos.add(cid);
  const sinCubrir = element.criterios.filter((c) => !cubiertos.has(c.id));

  const [showForm, setShowForm] = useState(false);
  const [formNombre, setFormNombre] = useState("");
  const [formTipo, setFormTipo] = useState<Instrumento["tipo"]>("checklist");
  const [formReactivos, setFormReactivos] = useState<number>(10);
  const [formCriterios, setFormCriterios] = useState<string[]>([]);

  const resetForm = () => {
    setFormNombre("");
    setFormTipo("checklist");
    setFormReactivos(10);
    setFormCriterios([]);
  };
  const toggleCriterio = (id: string) => {
    setFormCriterios((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const handleSave = () => {
    if (!formNombre.trim() || formReactivos <= 0 || formCriterios.length === 0) return;
    const newIns: Instrumento = {
      id: `in-${Date.now()}`,
      nombre: formNombre.trim(),
      tipo: formTipo,
      reactivos: formReactivos,
      elementoId: element.id,
      criterioIds: formCriterios,
    };
    upsertInstrument(standardCode, newIns);
    resetForm();
    setShowForm(false);
  };
  const handleRemove = (ins: Instrumento) => {
    if (!window.confirm(`¿Eliminar el instrumento "${ins.nombre}" de este elemento?`)) return;
    removeInstrument(standardCode, ins.id);
  };

  const colWidth = `minmax(160px, 1fr)`;
  const gridTemplate = `60px minmax(220px, 2fr) ${own.map(() => colWidth).join(" ")}`;
  const headerCell: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--cc-gray-600)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "10px 8px",
    background: "var(--cc-gray-50, #f8fafc)",
    borderBottom: "1px solid var(--cc-gray-200)",
  };
  const bodyCell: React.CSSProperties = {
    fontSize: 12,
    color: "var(--cc-gray-700)",
    padding: "10px 8px",
    borderBottom: "1px solid var(--cc-gray-100)",
    display: "flex",
    alignItems: "center",
  };

  const tipoOpciones: Instrumento["tipo"][] = ["checklist", "rúbrica", "examen", "observación"];

  const closeForm = () => { resetForm(); setShowForm(false); };
  const formCard = showForm && (
    <div
      onClick={closeForm}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 24,
      }}
      data-testid="modal-overlay-instrumento"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: "white", borderRadius: 12, width: "min(720px, 100%)",
          maxHeight: "90vh", overflowY: "auto", padding: 20,
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
          border: "1px solid var(--cc-gray-200)",
        }}
        data-testid="form-nuevo-instrumento"
      >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid var(--cc-gray-100)" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--cc-navy)" }}>Nuevo instrumento para {element.codigo}</div>
        <button onClick={closeForm} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-gray-500)" }} aria-label="Cerrar"><X size={18} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-700)" }}>Nombre del instrumento</span>
          <input
            data-testid="input-nombre-instrumento"
            value={formNombre}
            onChange={(e) => setFormNombre(e.target.value)}
            placeholder="Ej. Lista de cotejo de inspección"
            style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-300)", borderRadius: 6, fontSize: 13 }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-700)" }}>Tipo</span>
          <select
            data-testid="select-tipo-instrumento"
            value={formTipo}
            onChange={(e) => setFormTipo(e.target.value as Instrumento["tipo"])}
            style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-300)", borderRadius: 6, fontSize: 13, background: "white", textTransform: "capitalize" }}
          >
            {tipoOpciones.map((t) => (<option key={t} value={t}>{t}</option>))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-700)" }}>Reactivos</span>
          <input
            data-testid="input-reactivos-instrumento"
            type="number"
            min={1}
            value={formReactivos}
            onChange={(e) => setFormReactivos(Math.max(0, parseInt(e.target.value, 10) || 0))}
            style={{ padding: "8px 10px", border: "1px solid var(--cc-gray-300)", borderRadius: 6, fontSize: 13 }}
          />
        </label>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-700)", marginBottom: 6 }}>
          Criterios que aplica ({formCriterios.length}/{element.criterios.length})
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 6, maxHeight: 220, overflowY: "auto", padding: 6, background: "white", border: "1px solid var(--cc-gray-200)", borderRadius: 6 }}>
          {element.criterios.map((c) => {
            const checked = formCriterios.includes(c.id);
            return (
              <label key={c.id} data-testid={`check-criterio-${c.codigo}`} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: 6, borderRadius: 4, background: checked ? "var(--cc-teal-bg, #ecfeff)" : "transparent", cursor: "pointer" }}>
                <input type="checkbox" checked={checked} onChange={() => toggleCriterio(c.id)} style={{ marginTop: 3 }} />
                <span style={{ fontSize: 11, color: "var(--cc-gray-700)", lineHeight: 1.4 }}>
                  <strong style={{ color: "var(--cc-navy)" }}>{c.codigo}</strong> · {c.descripcion}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <PrimaryButton variant="outline" size="sm" onClick={() => { resetForm(); setShowForm(false); }}>Cancelar</PrimaryButton>
        <PrimaryButton
          variant="dark"
          size="sm"
          onClick={handleSave}
          testId="button-guardar-instrumento"
          disabled={!formNombre.trim() || formReactivos <= 0 || formCriterios.length === 0}
        >
          Guardar instrumento
        </PrimaryButton>
      </div>
      </div>
    </div>
  );

  if (own.length === 0) {
    return (
      <Card testId="tab-instrumentos">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <SectionTitle>Cobertura por criterio</SectionTitle>
          <PrimaryButton size="sm" variant="dark" onClick={() => setShowForm(true)} testId="button-agregar-instrumento"><Plus size={14} /> Agregar instrumento</PrimaryButton>
        </div>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)", marginTop: 8 }}>
          Este elemento todavía no tiene instrumentos configurados.
        </div>
        {formCard}
      </Card>
    );
  }

  return (
    <Card testId="tab-instrumentos">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 12, flexWrap: "wrap" }}>
        <SectionTitle>Cobertura por criterio</SectionTitle>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
            {own.length} instrumentos · {cubiertos.size}/{element.criterios.length} criterios cubiertos
          </span>
          <PrimaryButton size="sm" variant="dark" onClick={() => setShowForm(true)} testId="button-agregar-instrumento"><Plus size={14} /> Agregar instrumento</PrimaryButton>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 12, lineHeight: 1.5 }}>
        Mapa cruzado de criterios vs. instrumentos que los evalúan. Un criterio sin marca en ninguna columna queda sin evaluar.
      </div>

      <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 8, overflow: "hidden" }} data-testid="tabla-cobertura">
        <div style={{ display: "grid", gridTemplateColumns: gridTemplate }}>
          <div style={{ ...headerCell, textAlign: "left" }}>Código</div>
          <div style={{ ...headerCell, textAlign: "left" }}>Criterio</div>
          {own.map((ins) => {
            const Icon = instrumentIcons[ins.tipo];
            return (
              <div key={`h-${ins.id}`} style={{ ...headerCell, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, textTransform: "none", letterSpacing: 0, lineHeight: 1.3, position: "relative" }}>
                <button
                  onClick={() => handleRemove(ins)}
                  data-testid={`button-eliminar-instrumento-${ins.id}`}
                  title="Eliminar instrumento"
                  style={{ position: "absolute", top: 4, right: 4, background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-gray-400)", padding: 2, borderRadius: 4 }}
                >
                  <Trash2 size={12} />
                </button>
                <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", paddingRight: 16 }}>
                  <Icon size={14} color="var(--cc-teal)" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-navy)" }}>{ins.nombre}</span>
                </div>
                <span style={{ fontSize: 10, fontWeight: 500, color: "var(--cc-gray-500)", textTransform: "capitalize" }}>{ins.tipo} · {ins.reactivos} reactivos</span>
              </div>
            );
          })}

          {element.criterios.map((c) => {
            return (
              <React.Fragment key={c.id}>
                <div style={{ ...bodyCell, fontWeight: 700, color: "var(--cc-navy)" }}>{c.codigo}</div>
                <div style={{ ...bodyCell, lineHeight: 1.4 }}>{c.descripcion}</div>
                {own.map((ins, idx) => {
                  const covers = (ins.criterioIds ?? []).includes(c.id);
                  return (
                    <div key={`${c.id}-${idx}`} style={{ ...bodyCell, justifyContent: "center" }}>
                      {covers ? (
                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, borderRadius: 999, background: "var(--cc-teal-bg, #ccfbf1)", color: "var(--cc-teal, #0d9488)", fontSize: 13, fontWeight: 700 }}>✓</span>
                      ) : (
                        <span style={{ color: "var(--cc-gray-300, #cbd5e1)", fontSize: 14 }}>—</span>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {sinCubrir.length > 0 && (
        <div style={{ marginTop: 12, padding: 10, background: "var(--cc-amber-bg, #fef3c7)", borderRadius: 8, fontSize: 12, color: "var(--cc-gray-700)" }}>
          <strong>{sinCubrir.length}</strong> criterio(s) sin instrumento: {sinCubrir.map((c) => c.codigo).join(", ")}
        </div>
      )}

      {formCard}
    </Card>
  );
}

const familiaLabel: Record<string, string> = {
  RG: "Reglas generales",
  RE: "Reglas por evidencia",
  RL: "Reglas legales y normativas",
  RT: "Reglas técnicas",
  RP: "Reglas procedimentales",
  RV: "Reglas de validación cruzada",
  RD: "Reglas de decisión",
};

function ReglasTab({ rules }: { rules: Rule[] }) {
  if (rules.length === 0) {
    return (
      <Card>
        <SectionTitle>Reglas asociadas</SectionTitle>
        <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>Aún no hay reglas configuradas para este estándar.</div>
      </Card>
    );
  }
  const byFamily = new Map<string, Rule[]>();
  for (const r of rules) {
    const arr = byFamily.get(r.familia) ?? [];
    arr.push(r);
    byFamily.set(r.familia, arr);
  }
  return (
    <Card testId="tab-reglas">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <SectionTitle>Reglas asociadas</SectionTitle>
        <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{rules.length} reglas en {byFamily.size} familias</span>
      </div>
      <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 12, lineHeight: 1.5 }}>
        Las reglas se configuran a nivel estándar y se aplican durante la dictaminación. Una regla bloqueante impide cerrar el dictamen si no se cumple.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {Array.from(byFamily.entries()).map(([fam, items]) => (
          <div key={fam}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Badge tone="navy">{fam}</Badge>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)" }}>{familiaLabel[fam] ?? fam}</span>
              <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>· {items.length} reglas</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {items.map((r) => (
                <div key={r.id} data-testid={`rule-${r.id}`} style={{ padding: 10, border: "1px solid var(--cc-gray-100)", borderRadius: 8, display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <ShieldCheck size={14} color={r.severidad === "bloqueante" ? "#dc2626" : "#d97706"} style={{ marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{r.nombre}</div>
                    <div style={{ fontSize: 11, color: "var(--cc-gray-600)", marginTop: 2, lineHeight: 1.45 }}>{r.descripcion}</div>
                  </div>
                  <Badge tone={r.severidad === "bloqueante" ? "red" : "amber"}>{r.severidad}</Badge>
                  {!r.habilitada && <Badge tone="gray">deshabilitada</Badge>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function IaTab({ element }: { element: StandardElement }) {
  const guidance: { perfil: string; tono: "teal" | "navy" | "amber" | "green" | "red" | "gray"; mensaje: string }[] = [
    { perfil: "Candidato / Alumno", tono: "teal", mensaje: `Explica cada criterio del elemento ${element.codigo}, recomienda módulos LMS para reforzar y avisa de reactivos no contestados antes del examen.` },
    { perfil: "Instructor", tono: "navy", mensaje: `Detecta brechas grupales en los criterios de ${element.titulo.toLowerCase()} y sugiere prácticas adicionales antes de la evaluación formal.` },
    { perfil: "Evaluador", tono: "amber", mensaje: `Sugiere observaciones según los criterios marcados como “no cumple”, alerta criterios sin observación y propone evidencia complementaria.` },
    { perfil: "Administrador académico", tono: "navy", mensaje: `Alerta cuellos de botella en este elemento (candidatos atorados, evaluaciones programadas sin instrumento, sedes con baja capacidad).` },
    { perfil: "Dictaminador", tono: "red", mensaje: `Revisa que todos los criterios obligatorios del elemento estén evaluados, que las evidencias estén cargadas y que no haya inconsistencias entre observación y telemetría.` },
    { perfil: "Auditor", tono: "gray", mensaje: `Revisa trazabilidad, cambios en criterios, evidencias modificadas y bitácoras de evaluación del elemento.` },
  ];
  return (
    <Card testId="tab-ia">
      <SectionTitle>IA contextual por perfil</SectionTitle>
      <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 12, lineHeight: 1.5 }}>
        La IA usa el contenido del elemento y los criterios para asistir a cada perfil, sin sustituir al evaluador ni al dictaminador.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {guidance.map((g) => (
          <div key={g.perfil} style={{ padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Sparkles size={14} color="var(--cc-teal)" />
              <Badge tone={g.tono}>{g.perfil}</Badge>
            </div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>{g.mensaje}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

type PreviewRole = "candidato" | "instructor" | "evaluador" | "dictaminador";

const previewRoles: { id: PreviewRole; label: string; icon: React.ComponentType<{ size?: number; color?: string }>; descripcion: string }[] = [
  { id: "candidato", label: "Candidato", icon: GraduationCap, descripcion: "Lo que verá la persona que se está certificando" },
  { id: "instructor", label: "Instructor", icon: Users, descripcion: "Avance del grupo y temas a reforzar" },
  { id: "evaluador", label: "Evaluador", icon: ClipboardCheck, descripcion: "Instrumentos y carga de evidencia en sitio" },
  { id: "dictaminador", label: "Dictaminador", icon: Gavel, descripcion: "Validación final y consistencia documental" },
];

function VistaPreviaTab({ element, instruments }: { element: StandardElement; instruments: Instrumento[] }) {
  const [role, setRole] = useState<PreviewRole>("candidato");
  const own = instruments.filter((i) => i.elementoId === element.id);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card testId="tab-vista-previa">
        <SectionTitle>Vista previa por perfil</SectionTitle>
        <div style={{ fontSize: 12, color: "var(--cc-gray-600)", lineHeight: 1.5, marginBottom: 12 }}>
          Así verá <strong>{element.codigo}</strong> cada rol cuando el estándar esté publicado. Cambia de perfil para ver
          la información, las acciones y las alertas específicas que recibe cada usuario en su tablero.
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {previewRoles.map((r) => {
            const active = role === r.id;
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setRole(r.id)}
                data-testid={`role-${r.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: active ? "1px solid var(--cc-teal)" : "1px solid var(--cc-gray-200)",
                  background: active ? "var(--cc-teal-bg)" : "white",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <Icon size={18} color={active ? "var(--cc-teal)" : "var(--cc-gray-500)"} />
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: active ? "var(--cc-teal)" : "var(--cc-navy)" }}>{r.label}</div>
                  <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>{r.descripcion}</div>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {role === "candidato" && <PreviewCandidato element={element} />}
      {role === "instructor" && <PreviewInstructor element={element} />}
      {role === "evaluador" && <PreviewEvaluador element={element} instruments={own} />}
      {role === "dictaminador" && <PreviewDictaminador element={element} />}
    </div>
  );
}

function PreviewSection({
  icon: Icon,
  title,
  hint,
  children,
}: {
  icon: React.ComponentType<{ size?: number; color?: string }>;
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Icon size={14} color="var(--cc-teal)" />
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)", textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
      </div>
      {hint && <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginBottom: 8 }}>{hint}</div>}
      {children}
    </div>
  );
}

function PreviewCandidato({ element }: { element: StandardElement }) {
  const moduloRelacionado = element.criterios.find((c) => c.moduloLMS)?.moduloLMS ?? `Módulo ${element.codigo} — ${element.titulo}`;
  const evidenciasPendientes = element.evidencias.filter((e) => e.obligatoria);
  const recomendaciones = [
    `Repasa el módulo "${moduloRelacionado}" antes de tu próxima sesión presencial.`,
    `Sube tu ${evidenciasPendientes[0]?.nombre ?? "evidencia obligatoria"} con buena iluminación y audio claro.`,
    "Practica 10 minutos cada día con los simuladores recomendados para ganar fluidez.",
  ];
  return (
    <Card>
      <SectionTitle>Vista del candidato</SectionTitle>
      <PreviewSection icon={BookOpen} title="Módulo relacionado" hint="Lo que estudia el candidato antes de la evaluación">
        <div style={{ padding: 12, background: "var(--cc-teal-bg)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>{moduloRelacionado}</div>
            <div style={{ fontSize: 11, color: "var(--cc-gray-600)", marginTop: 2 }}>Ruta formativa · Duración estimada 35 min</div>
          </div>
          <PrimaryButton variant="ghost"><ArrowRight size={14} /> Ir al módulo</PrimaryButton>
        </div>
      </PreviewSection>

      <PreviewSection icon={ClipboardCheck} title="Criterios en lenguaje claro" hint="Qué se le evalúa y por qué importa">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {element.criterios.slice(0, 5).map((c) => (
            <div key={c.id} style={{ padding: 10, border: "1px solid var(--cc-gray-200)", borderRadius: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <Badge tone={tipoTone[c.tipo]}>{c.codigo}</Badge>
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombreCorto ?? c.descripcion.split(",")[0]}</span>
              </div>
              <div style={{ fontSize: 11, color: "var(--cc-gray-600)", lineHeight: 1.5 }}>
                Lo que se espera: <strong>{c.descripcion.toLowerCase()}</strong>. {c.critico ? "Es un criterio crítico, no puedes fallarlo." : "Suma a tu calificación final."}
              </div>
            </div>
          ))}
          {element.criterios.length > 5 && (
            <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>+{element.criterios.length - 5} criterios más en la vista completa del candidato.</div>
          )}
        </div>
      </PreviewSection>

      <PreviewSection icon={Upload} title="Evidencias pendientes" hint="Lo que el candidato debe subir antes de su cita">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {evidenciasPendientes.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>No hay evidencias obligatorias por subir.</div>
          ) : evidenciasPendientes.map((e) => {
            const Icon = evidenceIcons[e.tipo];
            return (
              <div key={e.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", border: "1px dashed var(--cc-gray-300)", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon size={14} color="var(--cc-gray-500)" />
                  <span style={{ fontSize: 12, color: "var(--cc-navy)" }}>{e.nombre}</span>
                </div>
                <Badge tone="amber">Pendiente</Badge>
              </div>
            );
          })}
        </div>
      </PreviewSection>

      <PreviewSection icon={Sparkles} title="Recomendaciones IA" hint="Sugerencias personalizadas según tu avance">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {recomendaciones.map((r, i) => (
            <div key={i} style={{ padding: "8px 12px", background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8, fontSize: 12, color: "var(--cc-gray-700)" }}>
              <Sparkles size={12} color="#EA580C" style={{ marginRight: 6, verticalAlign: "middle" }} />
              {r}
            </div>
          ))}
        </div>
      </PreviewSection>
    </Card>
  );
}

function PreviewInstructor({ element }: { element: StandardElement }) {
  // Avance mock determinista a partir del id del criterio
  const avance = element.criterios.map((c) => {
    const seed = c.id.split("").reduce((a, ch) => a + ch.charCodeAt(0), 0);
    const pct = 45 + (seed % 50); // 45–94
    return { c, pct };
  });
  const debiles = [...avance].sort((a, b) => a.pct - b.pct).slice(0, 3);
  const candidatosRiesgo = [
    { nombre: "Reyes Hernández, A.", grupo: "GN-2026-03", riesgo: "Sin evidencias obligatorias subidas" },
    { nombre: "Vázquez Téllez, J.", grupo: "GN-2026-03", riesgo: `Reprobó simulacro de ${debiles[0]?.c.codigo ?? "criterio crítico"}` },
    { nombre: "Domínguez Pérez, M.", grupo: "GN-2026-03", riesgo: "Inasistencia a 2 de 4 prácticas" },
  ];
  return (
    <Card>
      <SectionTitle>Vista del instructor</SectionTitle>
      <PreviewSection icon={ClipboardCheck} title="Avance del grupo por criterio" hint="Promedio de aciertos del grupo en cada criterio">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {avance.map(({ c, pct }) => {
            const color = pct < 60 ? "#DC2626" : pct < 80 ? "#D97706" : "#059669";
            return (
              <div key={c.id} style={{ display: "grid", gridTemplateColumns: "60px 1fr 50px", alignItems: "center", gap: 10 }}>
                <Badge tone={tipoTone[c.tipo]}>{c.codigo}</Badge>
                <div>
                  <div style={{ fontSize: 11, color: "var(--cc-gray-700)", marginBottom: 4, lineHeight: 1.4 }}>{c.nombreCorto ?? c.descripcion}</div>
                  <div style={{ height: 6, background: "var(--cc-gray-200)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: color }} />
                  </div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color, textAlign: "right" }}>{pct}%</div>
              </div>
            );
          })}
        </div>
      </PreviewSection>

      <PreviewSection icon={AlertTriangle} title="Temas débiles" hint="Criterios con menor promedio: refuerza con tu próxima sesión">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {debiles.map(({ c, pct }) => (
            <div key={c.id} style={{ padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)" }}>{c.codigo} · {c.nombreCorto ?? c.descripcion}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-600)", marginTop: 2 }}>Sugerencia IA: agendar repaso práctico de 30 min.</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#DC2626" }}>{pct}%</div>
            </div>
          ))}
        </div>
      </PreviewSection>

      <PreviewSection icon={Users} title="Candidatos en riesgo" hint="Personas que probablemente no aprueben sin intervención">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {candidatosRiesgo.map((cand, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--cc-navy)" }}>{cand.nombre}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-600)" }}>{cand.grupo} · {cand.riesgo}</div>
              </div>
              <Badge tone="amber">En riesgo</Badge>
            </div>
          ))}
        </div>
      </PreviewSection>
    </Card>
  );
}

function PreviewEvaluador({ element, instruments }: { element: StandardElement; instruments: Instrumento[] }) {
  const instrumento = instruments[0];
  const items = instrumento?.items ?? element.criterios.slice(0, 5).map((c) => ({ id: c.id, texto: c.descripcion, criterioId: c.id }));
  return (
    <Card>
      <SectionTitle>Vista del evaluador</SectionTitle>
      <PreviewSection icon={ClipboardCheck} title="Checklist / rúbrica" hint={instrumento ? `Instrumento activo: ${instrumento.nombre}` : "Instrumento de demostración"}>
        <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 10, overflow: "hidden" }}>
          {items.slice(0, 6).map((it, i) => (
            <div key={it.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, padding: "10px 12px", borderTop: i === 0 ? "none" : "1px solid var(--cc-gray-200)", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "var(--cc-navy)" }}>{i + 1}. {it.texto}</div>
              <div style={{ display: "flex", gap: 6 }}>
                <span style={{ padding: "4px 10px", borderRadius: 6, background: "var(--cc-teal-bg)", color: "var(--cc-teal)", fontSize: 11, fontWeight: 700 }}>Cumple</span>
                <span style={{ padding: "4px 10px", borderRadius: 6, background: "var(--cc-gray-100)", color: "var(--cc-gray-500)", fontSize: 11 }}>No cumple</span>
                <span style={{ padding: "4px 10px", borderRadius: 6, background: "var(--cc-gray-100)", color: "var(--cc-gray-500)", fontSize: 11 }}>N/A</span>
              </div>
            </div>
          ))}
        </div>
      </PreviewSection>

      <PreviewSection icon={MessageSquare} title="Campos de observación" hint="Notas del evaluador asociadas al instrumento">
        <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 10, padding: 12, fontSize: 12, color: "var(--cc-gray-500)", fontStyle: "italic", background: "var(--cc-gray-50, #FAFAFA)" }}>
          "Describe condiciones del piso, visibilidad y cualquier desviación detectada durante la maniobra…"
        </div>
      </PreviewSection>

      <PreviewSection icon={Upload} title="Carga de evidencia" hint="Foto, video o documento que respalde la evaluación">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {element.evidencias.map((e) => {
            const Icon = evidenceIcons[e.tipo];
            return (
              <div key={e.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 12px", border: "1px dashed var(--cc-gray-300)", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <Icon size={14} color="var(--cc-gray-500)" />
                  <span style={{ fontSize: 12, color: "var(--cc-navy)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.nombre}</span>
                </div>
                <Upload size={14} color="var(--cc-teal)" />
              </div>
            );
          })}
        </div>
      </PreviewSection>

      <PreviewSection icon={CheckCircle2} title="Cierre del instrumento" hint="Firma y envío al dictaminador">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: 12, background: "var(--cc-teal-bg)", borderRadius: 10 }}>
          <div style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>
            Una vez firmado, el instrumento se bloquea y pasa a la bandeja del dictaminador.
          </div>
          <PrimaryButton variant="dark"><CheckCircle2 size={14} /> Firmar y cerrar</PrimaryButton>
        </div>
      </PreviewSection>
    </Card>
  );
}

function PreviewDictaminador({ element }: { element: StandardElement }) {
  const cumplidos = element.criterios.slice(0, Math.max(1, element.criterios.length - 1));
  const pendiente = element.criterios[element.criterios.length - 1];
  const alertas = [
    `Falta evidencia: ${element.evidencias.find((e) => e.obligatoria)?.nombre ?? "Evidencia obligatoria"} no fue cargada por el evaluador.`,
    pendiente ? `Inconsistencia: ${pendiente.codigo} aparece como "Cumple" sin observación de respaldo.` : "Revisar metadata del instrumento principal.",
  ];
  const documental = [
    "Cédula de identificación del candidato (INE)",
    "Acta de aplicación firmada por el evaluador",
    "Bitácora de evidencias con sellos de tiempo",
    "Carta responsiva del centro evaluador",
  ];
  return (
    <Card>
      <SectionTitle>Vista del dictaminador</SectionTitle>
      <PreviewSection icon={CheckCircle2} title="Criterios cumplidos" hint="Resultado consolidado tras la evaluación">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {cumplidos.map((c) => (
            <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 8 }}>
              <CheckCircle2 size={14} color="#059669" />
              <span style={{ fontSize: 12, color: "var(--cc-navy)" }}><strong>{c.codigo}</strong> · {c.nombreCorto ?? c.descripcion.split(",")[0]}</span>
            </div>
          ))}
        </div>
      </PreviewSection>

      <PreviewSection icon={FileText} title="Evidencias" hint="Archivos cargados durante la evaluación">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {element.evidencias.map((e, i) => {
            const Icon = evidenceIcons[e.tipo];
            const ok = i % 3 !== 2;
            return (
              <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", border: "1px solid var(--cc-gray-200)", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Icon size={14} color="var(--cc-gray-500)" />
                  <span style={{ fontSize: 12, color: "var(--cc-navy)" }}>{e.nombre}</span>
                </div>
                <Badge tone={ok ? "green" : "amber"}>{ok ? "Recibida" : "Por revisar"}</Badge>
              </div>
            );
          })}
        </div>
      </PreviewSection>

      <PreviewSection icon={AlertTriangle} title="Alertas" hint="Hallazgos automáticos del motor de reglas">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {alertas.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, padding: "8px 12px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8 }}>
              <AlertTriangle size={14} color="#DC2626" />
              <span style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>{a}</span>
            </div>
          ))}
        </div>
      </PreviewSection>

      <PreviewSection icon={ShieldCheck} title="Checklist documental" hint="Documentos que deben acompañar al expediente">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {documental.map((d, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, cursor: "pointer" }}>
              <input type="checkbox" defaultChecked={i < 3} style={{ accentColor: "var(--cc-teal)" }} />
              <span style={{ fontSize: 12, color: "var(--cc-navy)" }}>{d}</span>
            </label>
          ))}
        </div>
      </PreviewSection>
    </Card>
  );
}

function subTabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    color: active ? "var(--cc-teal)" : "var(--cc-gray-600)",
    background: "transparent",
    border: "none",
    borderBottom: active ? "2px solid var(--cc-teal)" : "2px solid transparent",
    cursor: "pointer",
  };
}
